<template>
  <div
    v-if="isBoxSelectionMenu"
    class="deepbox-box-name pr-1 text-truncate"
    :class="{
      'deepbox-box-name-tablet': device?.isTablet,
      'flex-grow-1': !isDeadline,
      'deadline-box-name': isDeadline,
    }"
  >
    {{ isShared ? boxEntryName : boxName }}
    <div
      v-if="showBoxNameSubtitle"
      class="ml-0 text-truncate text-medium-emphasis"
      :class="{
        'box-company':
          isShared || isRecent || isFavourite || isBoxSelectionMenu,
      }"
    >
      {{ isShared ? boxName : boxEntryName }}
    </div>
  </div>
  <div
    v-else
    v-tooltip="tooltipData"
    class="deepbox-box-name pr-1 text-truncate"
    :class="{
      'deepbox-box-name-tablet': device?.isTablet,
      'flex-grow-1': !isDeadline,
      'deadline-box-name': isDeadline,
    }"
  >
    {{ isShared || isInbox || isDeadline ? boxDescription : boxName }}
    <div
      v-if="isShared || (isShared && !isDeadline) || showBoxNameSubtitle"
      class="ml-0 text-truncate text-medium-emphasis"
      :class="{
        'box-company':
          isShared || isRecent || isFavourite || isBoxSelectionMenu || isInbox,
      }"
    >
      {{ boxName }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import type { Box, BoxEntry } from '@deepcloud/api-deep-box'

// Unified type that reflects actual property access patterns in this component
type BoxProp = BoxEntry | Box

// Type guards
function isBoxEntry(box: BoxProp): box is BoxEntry {
  return 'deepBoxName' in box
}

function isBoxInstance(box: BoxProp): box is Box {
  return 'name' in box && !('deepBoxName' in box)
}

const props = defineProps({
  isShared: {
    type: Boolean,
    default: false,
  },
  isInbox: {
    type: Boolean,
    default: false,
  },
  isRecent: {
    type: Boolean,
    default: false,
  },
  isFavourite: {
    type: Boolean,
    default: false,
  },
  isDeadline: {
    type: Boolean,
    default: false,
  },
  box: {
    type: Object as PropType<BoxProp>,
    default: () => ({}) as BoxProp,
  },
  isBoxSelectionMenu: {
    type: Boolean,
    default: false,
  },
  showBoxNameSubtitle: {
    type: Boolean,
    default: false,
  },
})

const device = inject(DeviceKey)

const boxName = computed<string | undefined>(() => {
  if (isBoxEntry(props.box)) return props.box.boxName
  if (isBoxInstance(props.box)) return props.box.name
  return undefined
})

const boxEntryName = computed<string | undefined>(() => {
  if (isBoxEntry(props.box)) return props.box.deepBoxName ?? props.box.boxName
  return undefined
})

const boxDescription = computed<string | undefined>(() => {
  if (props.isShared) {
    return isBoxEntry(props.box) ? props.box.company?.displayName : undefined
  }
  if (
    props.isRecent ||
    props.isFavourite ||
    props.isBoxSelectionMenu ||
    props.isInbox ||
    props.isDeadline
  ) {
    if (!isBoxEntry(props.box)) return undefined
    return props.isBoxSelectionMenu ? props.box.deepBoxName : props.box.boxName
  }
  return undefined
})

const tooltipData = computed(() => {
  const text = props.isShared
    ? `${boxDescription.value} (${boxEntryName.value})`
    : (boxEntryName.value ?? '')

  return {
    text,
    location: 'top',
    offset: 3,
    openDelay: 300,
  }
})
</script>

<style scoped lang="scss">
.deepbox-box-name {
  line-height: 1rem;
  display: block;
  max-width: 165px !important;
  text-align: left !important;
}

.deadline-box-name {
  margin-left: 10px;
  display: block;
  max-width: 130px !important;
}

.box-company {
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 1;
  font-size: 10px;
  line-height: 0.8rem;
  text-align: left !important;
}

.deepbox-box-name-tablet {
  max-width: 145px !important;
}
</style>
